"use client";

import { Core } from "@faire/design-tokens";
import { Column, Row } from "@faire/web/slate/Layout";
import * as React from "react";
import styled, { css } from "styled-components";

import { Spacer } from "../spacing";
import {
  highVisSlateComponent,
  HIGH_VIS_FILTER,
} from "../utils/highVisSlateComponent";

export interface ListItemProps {
  className?: string;
  size: "small" | "medium";
  start?: React.ReactNode;
  middle?: React.ReactNode;
  end?: React.ReactNode;
  loading?: boolean;
}

/**
 * @trackcomponent
 * @link [Docs](https://slate.faire.team/slate/component/list-item)
 */
export const ListItem: React.FC<ListItemProps> = ({
  className,
  size,
  start,
  middle,
  end,
  loading,
}) =>
  loading ? (
    <LoadingListItem size={size} />
  ) : (
    <ListItemContainer className={className} $size={size}>
      {start ? (
        <StartContainer $spaceRight={!!middle || !!end}>{start}</StartContainer>
      ) : null}
      {middle ? (
        <>
          <MiddleContainer justify="center" $spaceRight={!!end}>
            {middle}
          </MiddleContainer>
        </>
      ) : null}
      {end ? <EndContainer>{end}</EndContainer> : null}
    </ListItemContainer>
  );

const LoadingListItem: React.FC<{ size: "small" | "medium" }> = ({ size }) => (
  <ListItemContainer $size={size}>
    <StartContainer $spaceRight>
      <StartPlaceholder />
    </StartContainer>
    <MiddleContainer justify="center" $spaceRight>
      <MiddlePlaceholder />
      {size === "medium" && (
        <>
          <Spacer size="1x" />
          <MiddleSecondaryPlaceholder />
        </>
      )}
    </MiddleContainer>
    <EndContainer>
      <EndPlaceholder />
    </EndContainer>
  </ListItemContainer>
);

const PlaceholderStyles = css`
  border-radius: 2px;
  background-color: ${Core.surface.subdued};
`;

const StartPlaceholder = styled.div`
  width: 24px;
  height: 24px;
  ${PlaceholderStyles};
`;

const MiddlePlaceholder = styled.div`
  width: 100%;
  height: 10px;
  ${PlaceholderStyles};
`;

const MiddleSecondaryPlaceholder = styled.div`
  width: 20%;
  height: 10px;
  ${PlaceholderStyles};
`;

const EndPlaceholder = styled.div`
  width: 16px;
  height: 16px;
  ${PlaceholderStyles};
`;

const StartContainer = styled(Column).attrs({ justify: "center" })<{
  $spaceRight?: boolean;
}>`
  ${({ $spaceRight: spaceRight }) => (spaceRight ? "margin-right: 16px;" : "")}
`;

const MiddleContainer = styled(Column).attrs({ justify: "center" })<{
  $spaceRight?: boolean;
}>`
  flex: 1;
  min-width: 0;
  ${({ $spaceRight }) => ($spaceRight ? "margin-right: 16px;" : "")}
`;

const EndContainer = styled(Column).attrs({ justify: "center" })`
  justify-self: flex-end;
`;

const ListItemContainer = styled(Row).attrs({ align: "center" })<{
  $size: "small" | "medium";
}>`
  box-sizing: border-box;
  padding: ${(props) => (props.$size === "medium" ? "8px" : "0")} 16px;
  min-height: ${(props) => (props.$size === "medium" ? "64px" : "40px")};
  border-bottom: 1px solid ${Core.border.muted};
  background-color: ${Core.surface.inverse};
  &:hover {
    background-color: ${Core.surface.subdued};
  }

  &:active {
    background-color: ${Core.surface.subdued};
  }
  ${highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT)}
`;
